// Worker page section: retention purge anomalies (E-3 L7 round 5; owner ruling D1-40 (3); IF-53 web half).
//
// A read-only list of the open anomaly findings the monitor pages (`<module>.retention.purge-anomaly`, and
// `customers.retention.hold-window-deletions` for a tenant hold placed next to a row-spec purge batch), with an
// "Acknowledge" action per row: a confirm step, then POST /api/worker/retention-anomalies/ack, and the result word shown
// (acknowledged, or already_acknowledged on a repeat). The action is visible only with WORKER_OPERATE (the server enforces
// it too); the list needs WORKER_READ. The server returns ids, module, tenant, subject key, times and a reason code only,
// so nothing here can show a filename, path or note. Acknowledging a flagged row returns it to the purge claim: confirm
// with Legal first (docs/runbooks/retention-purges.md, "Acknowledging an anomaly").
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@heyroot/ui/primitives/Badge';
import { Button } from '@heyroot/ui/primitives/Button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@heyroot/ui/primitives/Table';
import { PERMS } from '../../../../rbac/permissions';
import { authenticatedFetch } from '../../../lib/authenticated-fetch';
import { workerQueryKeys } from '../../../lib/worker-api';
import type { WorkerSectionWeb } from '../sections';
import {
  ActionOutcome,
  QueryState,
  SectionHeading,
  SeverityBadge,
  fmtTs,
  useCanOperate,
} from '../worker-ui';

const BASE = '/api/worker/retention-anomalies';
const QUERY_KEY = [...workerQueryKeys.all, 'retention-anomalies'] as const;

export interface RetentionAnomalyRow {
  id: string;
  module: string;
  assertion: string;
  subjectKey: string;
  tenantId: string | null;
  severity: string;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  reason: string | null;
}

/** The server's own refusal text is written for operators and shown as is (same shape as lib/worker-api.ts). */
async function readJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let message = `Request failed: ${res.status}`;
    try {
      const b = (await res.json()) as { message?: unknown };
      if (typeof b.message === 'string' && b.message) message = b.message;
    } catch {
      // non-JSON error body: status-only message
    }
    throw new Error(message);
  }
  return res.json() as Promise<T>;
}

const fetchAnomalies = async (): Promise<{ anomalies: RetentionAnomalyRow[] }> =>
  readJson(await authenticatedFetch(BASE));

const ackAnomaly = async (a: RetentionAnomalyRow): Promise<{ result: string }> =>
  readJson(
    await authenticatedFetch(`${BASE}/ack`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ module: a.module, tenantId: a.tenantId, subjectKey: a.subjectKey }),
    }),
  );

function RetentionAnomaliesSection() {
  const canOperate = useCanOperate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: QUERY_KEY, queryFn: fetchAnomalies });
  const [confirming, setConfirming] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const ack = useMutation({
    mutationFn: ackAnomaly,
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: workerQueryKeys.all });
    },
  });
  const rows = q.data?.anomalies ?? [];

  return (
    <section className="flex flex-col gap-3" aria-label="Retention anomalies">
      <SectionHeading
        title="Retention anomalies"
        description="Purge anomalies the monitor is paging: a hold that arrived while an object delete was in flight, a lapsed in-flight lease, or a tenant hold placed next to a row purge batch. Acknowledge only after Legal has decided; an acknowledged row returns to the purge claim."
      />
      <ActionOutcome error={ack.error} success={ack.error ? null : success} />
      <QueryState
        isPending={q.isPending}
        isError={q.isError}
        isEmpty={rows.length === 0}
        emptyTitle="No open retention anomalies"
        onRetry={() => void q.refetch()}
      >
        <div className="overflow-x-auto">
          <Table dense>
            <TableHead>
              <tr>
                <TableHeaderCell>Module</TableHeaderCell>
                <TableHeaderCell>Subject</TableHeaderCell>
                <TableHeaderCell>Tenant</TableHeaderCell>
                <TableHeaderCell>Kind</TableHeaderCell>
                <TableHeaderCell>Severity</TableHeaderCell>
                <TableHeaderCell>First seen</TableHeaderCell>
                <TableHeaderCell>Last seen</TableHeaderCell>
                {canOperate && <TableHeaderCell>Actions</TableHeaderCell>}
              </tr>
            </TableHead>
            <TableBody>
              {rows.map((a) => (
                <TableRow key={a.id}>
                  <TableCell>{a.module}</TableCell>
                  <TableCell>{a.subjectKey}</TableCell>
                  <TableCell>{a.tenantId ?? 'platform'}</TableCell>
                  <TableCell>
                    <Badge variant="neutral">
                      {a.reason ??
                        (a.assertion.endsWith('hold-window-deletions')
                          ? 'hold near purge batch'
                          : 'unknown')}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <SeverityBadge severity={a.severity} />
                  </TableCell>
                  <TableCell>{fmtTs(a.firstSeenAt)}</TableCell>
                  <TableCell>{fmtTs(a.lastSeenAt)}</TableCell>
                  {canOperate && (
                    <TableCell>
                      {confirming === a.id ? (
                        <div className="flex items-center gap-1 flex-wrap">
                          <span className="text-caption text-muted">
                            Acknowledge {a.subjectKey}? Legal has decided.
                          </span>
                          <Button
                            size="sm"
                            variant="emphasis"
                            disabled={ack.isPending || a.tenantId === null}
                            onClick={() =>
                              ack.mutate(a, {
                                onSuccess: (res) =>
                                  setSuccess(`Acknowledge ${a.subjectKey}: ${res.result}`),
                                onSettled: () => setConfirming(null),
                              })
                            }
                          >
                            Confirm
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={ack.isPending}
                            onClick={() => setConfirming(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={ack.isPending || a.tenantId === null}
                          onClick={() => setConfirming(a.id)}
                        >
                          Acknowledge
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </QueryState>
    </section>
  );
}

export const section: WorkerSectionWeb = {
  key: 'retention-anomalies',
  order: 80,
  label: 'Retention anomalies',
  perm: PERMS.WORKER_READ,
  Component: RetentionAnomaliesSection,
};
